import api from './api';

export interface GmailCredential {
  id: number;
  is_active: boolean;
  last_sync: string | null;
  created_at: string;
}

export interface EmailTransaction {
  id: number;
  email_id: string;
  email_subject: string;
  email_from: string;
  email_date: string;
  email_type: string;
  merchant_name: string | null;
  transaction_date: string | null;
  amount: number | null;
  currency: string;
  tax_amount: number | null;
  invoice_id: string | null;
  order_id: string | null;
  category: string;
  is_processed: boolean;
  confidence_score: number;
  created_at: string;
}

export interface Bill {
  id: number;
  title: string;
  merchant_name: string;
  bill_date: string;
  amount: number;
  currency: string;
  tax_amount: number | null;
  invoice_number: string | null;
  category: string;
  file_path: string;
  file_url: string | null;
  file_type: string;
  tags: string[];
  notes: string | null;
  created_at: string;
}

export interface ExpenseClaim {
  id: number;
  title: string;
  description: string | null;
  status: string;
  start_date: string;
  end_date: string;
  total_amount: number;
  currency: string;
  reimbursed_amount: number;
  expenses_count: number;
  bills_count: number;
  submitted_at: string | null;
  approved_at: string | null;
  reimbursed_at: string | null;
  created_at: string;
}

export interface TaxRecord {
  id: number;
  tax_type: string;
  tax_amount: number;
  currency: string;
  tax_date: string;
  tax_rate: number | null;
  base_amount: number | null;
  merchant_name: string;
  is_business_expense: boolean;
  is_tax_deductible: boolean;
  notes: string | null;
  created_at: string;
}

export interface ExpenseSummary {
  total_spent: number;
  currency: string;
  period_days: number;
  by_category: Record<string, { name: string; amount: number; count: number }>;
  top_merchants: Array<{ merchant: string; amount: number; count: number }>;
}

export interface ExpenseDashboard {
  period_days: number;
  total_spent: number;
  currency: string;
  transaction_count: number;
  average_transaction: number;
  monthly_breakdown: Record<string, { amount: number; count: number }>;
  category_breakdown: Record<string, { name: string; amount: number; percentage: number }>;
  alerts?: SpendingAlert[];
  alerts_unread_count?: number;
}

export interface SpendingAlert {
  id: number;
  email_transaction: number | null;
  alert_type: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  title: string;
  message: string;
  metadata: Record<string, any>;
  is_read: boolean;
  created_at: string;
}

export const gmailExpensesService = {
  // Gmail OAuth
  async initiateOAuth(): Promise<{ authorization_url: string; state: string }> {
    // Get current origin for redirect URI
    const redirectUri = `${window.location.origin}/gmail-oauth-callback`;
    const response = await api.get('/gmail-expenses/oauth/authorize/', {
      params: { redirect_uri: redirectUri }
    });
    return response.data;
  },

  async completeOAuth(code: string, state: string): Promise<{ message: string; credential_id: number }> {
    // Get current origin for redirect URI (must match authorization)
    const redirectUri = `${window.location.origin}/gmail-oauth-callback`;
    const response = await api.post('/gmail-expenses/oauth/callback/', { 
      code, 
      state,
      redirect_uri: redirectUri
    });
    return response.data;
  },

  // Credentials
  async getCredentials(): Promise<GmailCredential | null> {
    try {
      const response = await api.get('/gmail-expenses/credentials/');
      return response.data.length > 0 ? response.data[0] : null;
    } catch {
      return null;
    }
  },

  async disconnectGmail(): Promise<void> {
    await api.post('/gmail-expenses/credentials/disconnect/');
  },

  async syncEmails(): Promise<{ message: string; status: string }> {
    const response = await api.post('/gmail-expenses/credentials/sync/');
    return response.data;
  },

  // Transactions
  async getTransactions(params?: {
    category?: string;
    start_date?: string;
    end_date?: string;
    merchant?: string;
  }): Promise<EmailTransaction[]> {
    const response = await api.get('/gmail-expenses/transactions/', { params });
    return response.data;
  },

  async getTransactionSummary(days: number = 30): Promise<ExpenseSummary> {
    const response = await api.get('/gmail-expenses/transactions/summary/', {
      params: { days },
    });
    return response.data;
  },

  // Bills
  async getBills(params?: {
    category?: string;
    merchant?: string;
    tag?: string;
    search?: string;
  }): Promise<Bill[]> {
    const response = await api.get('/gmail-expenses/bills/', { params });
    return response.data;
  },

  async getBill(id: number): Promise<Bill> {
    const response = await api.get(`/gmail-expenses/bills/${id}/`);
    return response.data;
  },

  async updateBill(id: number, data: Partial<Bill>): Promise<Bill> {
    const response = await api.patch(`/gmail-expenses/bills/${id}/`, data);
    return response.data;
  },

  async deleteBill(id: number): Promise<void> {
    await api.delete(`/gmail-expenses/bills/${id}/`);
  },

  // Expense Claims
  async getClaims(params?: { status?: string }): Promise<ExpenseClaim[]> {
    const response = await api.get('/gmail-expenses/claims/', { params });
    return response.data;
  },

  async createClaim(data: {
    title: string;
    description?: string;
    start_date: string;
    end_date: string;
    expenses?: number[];
    bills?: number[];
  }): Promise<ExpenseClaim> {
    const response = await api.post('/gmail-expenses/claims/', data);
    return response.data;
  },

  async submitClaim(id: number): Promise<void> {
    await api.post(`/gmail-expenses/claims/${id}/submit/`);
  },

  async exportClaim(id: number): Promise<void> {
    await api.post(`/gmail-expenses/claims/${id}/export/`);
  },

  // Tax Records
  async getTaxRecords(params?: { tax_type?: string; year?: number }): Promise<TaxRecord[]> {
    const response = await api.get('/gmail-expenses/taxes/', { params });
    return response.data;
  },

  async getTaxSummary(year: number = new Date().getFullYear()): Promise<{
    year: number;
    direct_tax: number;
    indirect_tax: number;
    total_tax: number;
    tax_deductible: number;
    currency: string;
  }> {
    const response = await api.get('/gmail-expenses/taxes/summary/', {
      params: { year },
    });
    return response.data;
  },

  // Dashboard
  async getDashboard(days: number = 30): Promise<ExpenseDashboard> {
    const response = await api.get('/gmail-expenses/dashboard/', {
      params: { days },
    });
    return response.data;
  },
};
